import { useState } from "react";
import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
//  ADMIN LOGIN — Supabase Auth (email + password)
//  Replaces the old phone + client-side SHA-256 version (which was bypassable).
//  Only users listed in the admin_roles table can get in.
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminLogin({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // 1. Sign in with Supabase Auth.
      const { data, error: signInErr } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInErr || !data?.user) {
        setError("Invalid email or password.");
        setLoading(false);
        return;
      }

      // 2. Confirm this user is actually an admin (listed in admin_roles).
      const { data: adminRow, error: roleErr } = await supabase
        .from("admin_roles")
        .select("name, email")
        .eq("user_id", data.user.id)
        .single();

      if (roleErr || !adminRow) {
        // Signed in, but not an admin — sign them straight back out.
        await supabase.auth.signOut();
        setError("This account does not have admin access.");
        setLoading(false);
        return;
      }

      // 3. Hand the session up to AdminRoute.
      onLogin({
        name: adminRow.name || "Admin",
        email: adminRow.email || data.user.email,
        role: "admin",
        userId: data.user.id,
      });
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "#0F2D5C", padding: 24,
    }}>
      <div style={{
        background: "white", borderRadius: 24, padding: 32,
        width: "100%", maxWidth: 380, boxShadow: "0 24px 64px rgba(0,0,0,.3)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, background: "#0F2D5C", borderRadius: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, margin: "0 auto 12px",
          }}>🏠</div>
          <div style={{ fontWeight: 900, fontSize: 22, color: "#0F2D5C" }}>GharPe Admin</div>
          <div style={{ fontSize: 12, color: "#9A9A9A", marginTop: 4 }}>Authorized Personnel Only</div>
        </div>

        {error && (
          <div style={{
            background: "#FEE2E2", border: "1px solid #FECACA",
            borderRadius: 12, padding: "10px 14px", marginBottom: 16,
            fontSize: 13, color: "#DC2626", textAlign: "center",
          }}>
            🔒 {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#545454", display: "block", marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="admin@example.com" required autoComplete="username"
              style={{
                width: "100%", padding: "13px 16px", borderRadius: 12,
                border: "1.5px solid #E8E8E8", fontSize: 15, boxSizing: "border-box",
                outline: "none", fontFamily: "inherit", color: "#0D0D0D",
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#545454", display: "block", marginBottom: 6 }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter password" required autoComplete="current-password"
                style={{
                  width: "100%", padding: "13px 46px 13px 16px", borderRadius: 12,
                  border: "1.5px solid #E8E8E8", fontSize: 15, boxSizing: "border-box",
                  outline: "none", fontFamily: "inherit", color: "#0D0D0D",
                }}
              />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{
                  position: "absolute", right: 14, top: "50%",
                  transform: "translateY(-50%)", background: "none",
                  border: "none", cursor: "pointer", fontSize: 18, color: "#9A9A9A",
                }}>
                {showPass ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !email || !password}
            style={{
              width: "100%", padding: 14,
              background: loading ? "#9A9A9A" : "#0F2D5C",
              color: "white", border: "none", borderRadius: 12,
              fontWeight: 800, fontSize: 16, cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit", transition: "all .2s",
            }}>
            {loading ? "Verifying..." : "Login →"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: "#C0C0C0" }}>
          This is a restricted area. All access attempts are logged.
        </div>
      </div>
    </div>
  );
}
